import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import pigeonIcon from '../assets/icons/pigeon_icon.png';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  FormatListBulleted as ListIcon,
  Add as AddIcon,
  Leaderboard as LeaderboardIcon,
  MenuBook as DiaryIcon,
  Science as ScienceIcon,
  AutoStories as LearnIcon,
  Settings as SettingsIcon,
  MoveToInbox as InboxIcon,
  PictureAsPdf as PdfIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import { IS_WEB } from '../services/platform';

const drawerWidth = 248;

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Mobile-only slide-in menu state; the desktop drawer is permanent and
  // ignores it entirely.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Real installed version from the main process (same source Settings
  // uses). On the web there's no meaningful version number — the deployed
  // app is always current — so the footer line is hidden there.
  const [appVersion, setAppVersion] = useState('');
  useEffect(() => {
    if (window.electronAPI && window.electronAPI.getAppVersion) {
      window.electronAPI.getAppVersion().then(setAppVersion).catch(() => {});
    }
  }, []);

  // Unread "Shared with me" count (web-only). Loaded once on mount and
  // refreshed whenever the inbox page accepts/declines a share (it dispatches
  // 'ppm-shares-changed').
  const [shareCount, setShareCount] = useState(0);
  useEffect(() => {
    if (!IS_WEB) return undefined;
    let alive = true;
    const refresh = () => {
      DatabaseService.getIncomingShares()
        .then((rows) => { if (alive) setShareCount(rows.length); })
        .catch(() => {});
    };
    refresh();
    window.addEventListener('ppm-shares-changed', refresh);
    return () => { alive = false; window.removeEventListener('ppm-shares-changed', refresh); };
  }, []);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'All Birds', icon: <ListIcon />, path: '/birds' },
    { text: 'Add Bird', icon: <AddIcon />, path: '/birds/add' },
    { text: 'Season Performance', icon: <LeaderboardIcon />, path: '/season' },
    { text: 'Weekly Diary', icon: <DiaryIcon />, path: '/diary' },
    { text: 'Test Pedigrees', icon: <ScienceIcon />, path: '/test-pedigrees' },
    { text: 'Learn & Tools', icon: <LearnIcon />, path: '/resources' },
    ...(IS_WEB ? [{ text: 'Downloaded Pedigrees', icon: <PdfIcon />, path: '/downloads' }] : []),
    ...(IS_WEB ? [{
      text: 'Shared with me',
      icon: (
        <Badge badgeContent={shareCount} color="error" overlap="circular">
          <InboxIcon />
        </Badge>
      ),
      path: '/shared'
    }] : []),
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
  ];

  // A route is active when it exactly matches, or (for section roots like
  // /birds) when the current path is nested beneath it.
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/birds/add') return location.pathname === '/birds/add';
    if (path === '/birds') {
      return location.pathname.startsWith('/birds') && location.pathname !== '/birds/add';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const brand = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(140deg, #eaf1fb 0%, #dbe7f7 100%)',
          border: '1px solid', borderColor: 'primary.lighter'
        }}
      >
        <img src={pigeonIcon} alt="" style={{ width: 28, height: 28 }} />
      </Box>
      <Box sx={{ lineHeight: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em', color: 'text.primary' }}>
          Pigeon Pedigree
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', color: 'primary.main', textTransform: 'uppercase' }}>
          Manager Pro
        </Typography>
      </Box>
    </Box>
  );

  // Shared drawer content — identical in the permanent (desktop) and
  // temporary (mobile) drawers so the two can never drift apart.
  const drawerContent = (
    <>
      {/* Brand lockup */}
      <Box sx={{ px: 2.5, py: 2.75 }}>{brand}</Box>

      <Typography
        sx={{
          px: 3, pt: 1.5, pb: 0.5, fontSize: '0.66rem', fontWeight: 700,
          letterSpacing: '0.09em', textTransform: 'uppercase', color: 'text.disabled'
        }}
      >
        Menu
      </Typography>

      <List sx={{ px: 1.5, pt: 0.5 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  py: 1.1,
                  px: 1.5,
                  color: active ? 'primary.contrastText' : 'text.secondary',
                  backgroundColor: active ? 'primary.main' : 'transparent',
                  boxShadow: active ? '0 4px 12px -3px rgba(44,90,160,0.45)' : 'none',
                  '&:hover': {
                    backgroundColor: active ? 'primary.dark' : 'action.hover'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: active ? 'primary.contrastText' : 'text.disabled'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: active ? 700 : 500,
                    letterSpacing: '-0.005em'
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ mt: 'auto', px: 3, py: 2 }}>
        {appVersion && (
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 500 }}>
            Version {appVersion}
          </Typography>
        )}
      </Box>
    </>
  );

  const drawerPaperSx = {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    borderRight: '1px solid',
    borderColor: 'divider'
  };

  return (
    <>
      {/* Small screens only: top bar with a menu button. Hidden on md+ so
          the desktop layout is untouched. */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          backgroundColor: '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            edge="start"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
          {brand}
        </Toolbar>
      </AppBar>

      {/* Small screens only: slide-in menu */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': drawerPaperSx
        }}
      >
        {drawerContent}
      </Drawer>

      {/* md and up: the permanent sidebar, exactly as before */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': drawerPaperSx
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Navigation;
